// Libs
import React, { useContext, useRef, useState } from 'react';
// Context
import { ModalBuilderContext } from 'core/context';
// Hooks
import useTheme from 'hooks/useTheme';
// Components
import { Modal, TextField, DatePicker, TimePicker } from 'components';
import AntButton from 'components/AntButton/AntButton';
// Styles
import styles from './UpdateOrderEvent.module.css';

const { TextArea } = TextField;

const COMMENT_HELP_ID = 'update-order-event-comment-help';
const SCHEDULE_HELP_ID = 'update-order-event-schedule-help';

const getThemeVariables = theme => ({
	'--event-modal-background': theme._primaryBackgroundColor,
	'--event-modal-surface': theme._secondaryBackgroundColor,
	'--event-modal-elevated-surface': theme._surfaceBackgroundColor,
	'--event-modal-text': theme._highEmphasisColor,
	'--event-modal-secondary-text': theme._mediumEmphasisColor,
	'--event-modal-inactive-text': theme._inactiveColor,
	'--event-modal-border': theme._borderColor,
	'--event-modal-accent': theme._secondaryColor,
	'--event-modal-warning': theme._warningColor,
});

const UpdateOrderEvent = () => {
	const {
		payload: { rowData, option, field, isSetting, onSetEventAssignment },
		cleanModal,
	} = useContext(ModalBuilderContext);
	const { theme, themeProfile } = useTheme();
	const [state, _setState] = useState({
		reprogramDate: undefined,
		reprogramTime: undefined,
		comment: '',
	});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const submittingRef = useRef(false);
	const setState = newProps =>
		_setState(current => ({ ...current, ...newProps }));
	const rows = Array.isArray(rowData) ? rowData : [rowData];
	const selectedCount = rows.length;
	const isBulk = selectedCount > 1;
	const requiresSchedule = !!option.props?.reprogram;
	const orderLabel = isBulk
		? `${selectedCount} órdenes seleccionadas`
		: rowData?.odtId || '1 orden seleccionada';
	const normalizedComment = state.comment.trim();
	const isIncomplete =
		!normalizedComment ||
		(requiresSchedule && (!state.reprogramDate || !state.reprogramTime));
	const isBusy = !!isSetting || isSubmitting;

	const close = () => {
		if (!isBusy) cleanModal();
	};
	const handleKeyDownCapture = event => {
		if (event.key !== 'Escape' || isBusy) return;
		event.preventDefault();
		event.stopPropagation();
		close();
	};

	const submit = async event => {
		event.preventDefault();
		if (isIncomplete || submittingRef.current || isSetting) return;
		submittingRef.current = true;
		setIsSubmitting(true);
		const wasSubmitted = await onSetEventAssignment(
			{
				...state,
				comment: normalizedComment,
				field,
				rowData,
				option,
			},
			cleanModal,
		);
		if (!wasSubmitted) {
			submittingRef.current = false;
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			className={styles.eventModal}
			title='Establecer evento'
			aria-label='Establecer evento de orden'
			centered
			visible
			width={560}
			destroyOnClose
			onCancel={close}
			footer={null}
			closable={!isBusy}
			maskClosable={!isBusy}
			keyboard={!isBusy}
		>
			<form
				className={styles.form}
				style={getThemeVariables(theme)}
				data-theme={themeProfile}
				data-testid='update-order-event-modal'
				onSubmit={submit}
				onKeyDownCapture={handleKeyDownCapture}
				aria-busy={isBusy}
				noValidate
			>
				<div className={styles.content}>
					<p className={styles.intro}>
						Revise el alcance antes de confirmar el cambio.
					</p>

					<section
						className={styles.selectionSummary}
						aria-label='Resumen del cambio de evento'
					>
						<div className={styles.summaryItem}>
							<span className={styles.summaryLabel}>Aplicar a</span>
							<strong className={styles.summaryValue}>{orderLabel}</strong>
						</div>
						<span className={styles.summaryArrow} aria-hidden='true'>
							→
						</span>
						<div className={styles.summaryItem}>
							<span className={styles.summaryLabel}>Nuevo evento</span>
							<strong className={styles.summaryValue}>{option.name}</strong>
						</div>
					</section>

					{requiresSchedule && (
						<fieldset className={styles.fieldset} disabled={isBusy}>
							<legend className={styles.legend}>Programación</legend>
							<p className={styles.help} id={SCHEDULE_HELP_ID}>
								Defina la nueva fecha y hora para{' '}
								{isBulk ? 'las órdenes seleccionadas' : 'la orden'}.
							</p>
							<div className={styles.scheduleGrid}>
								<div className={styles.fieldGroup}>
									<label
										className={styles.label}
										htmlFor='event-reprogram-date'
									>
										Fecha <span aria-hidden='true'>*</span>
									</label>
									<DatePicker
										id='event-reprogram-date'
										className={styles.picker}
										placeholder='Seleccione una fecha'
										value={state.reprogramDate}
										onChange={value => setState({ reprogramDate: value })}
										disabled={isBusy}
										aria-required='true'
										aria-describedby={SCHEDULE_HELP_ID}
									/>
								</div>
								<div className={styles.fieldGroup}>
									<label
										className={styles.label}
										htmlFor='event-reprogram-time'
									>
										Hora <span aria-hidden='true'>*</span>
									</label>
									<TimePicker
										id='event-reprogram-time'
										className={styles.picker}
										placeholder='Seleccione una hora'
										value={state.reprogramTime}
										onChange={value => setState({ reprogramTime: value })}
										disabled={isBusy}
										aria-required='true'
										aria-describedby={SCHEDULE_HELP_ID}
									/>
								</div>
							</div>
						</fieldset>
					)}

					<section className={styles.fieldGroup}>
						<label className={styles.label} htmlFor='event-comment'>
							Comentario <span aria-hidden='true'>*</span>
						</label>
						<p className={styles.help} id={COMMENT_HELP_ID}>
							Explique brevemente el motivo del cambio.
						</p>
						<TextArea
							id='event-comment'
							className={styles.commentInput}
							placeholder='Escriba un comentario'
							rows={3}
							height='auto'
							value={state.comment}
							onChange={event => setState({ comment: event.target.value })}
							disabled={isBusy}
							required
							aria-required='true'
							aria-describedby={COMMENT_HELP_ID}
						/>
					</section>
				</div>

				<footer className={styles.footer} aria-label='Acciones del evento'>
					<AntButton
						className={`${styles.actionButton} ${styles.secondaryButton}`}
						htmlType='button'
						data-action='cancel'
						onClick={close}
						disabled={isBusy}
					>
						Cancelar
					</AntButton>
					<AntButton
						className={`${styles.actionButton} ${styles.primaryButton}`}
						htmlType='submit'
						type='primary'
						data-action='confirm'
						loading={isBusy}
						disabled={isIncomplete || isBusy}
					>
						{isBusy ? 'Guardando…' : 'Confirmar cambio'}
					</AntButton>
				</footer>
			</form>
		</Modal>
	);
};

export default UpdateOrderEvent;
